/**
 * One named label (ADR-039): who is on it, who manages it, and who was given
 * access to its documents.
 *
 * Reading a labelled document needs a place on the label AND the document's
 * tier, so each person's workspace role is shown beside them. Owners and
 * admins are on no label by default; one who is not on it may add themselves,
 * which the label's people and the other owners are told about (an admin's
 * waits for another owner or admin).
 *
 * The server decides every answer; the screen offers only what the caller's
 * place suggests they may do, and says why a refusal happened.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  VAULT_NAMED_LABEL_DESCRIPTION_MAX,
  VAULT_NAMED_LABEL_NAME_MAX,
  type VaultHolderMemberDto,
  type VaultNamedLabelGroupDto,
  type VaultNamedLabelReaderDto,
  type VaultNamedLabelSummaryDto,
} from '@/lib/api';
import { StepUpDialog } from '../../components/StepUpDialog';
import {
  addVaultNamedLabelReader,
  allowVaultNamedLabelAiReading,
  describeNamedLabelError,
  namedLabelErrorCode,
  recoverVaultNamedLabel,
  useDeleteVaultNamedLabel,
  useDeprecateVaultNamedLabel,
  useRemoveVaultNamedLabelReader,
  useSetVaultNamedLabelManager,
  useStopVaultNamedLabelAiReading,
  useUpdateVaultNamedLabel,
  useVaultNamedLabelAccessLog,
  useVaultNamedLabelReaders,
  useVaultNamedLabelsInvalidator,
} from '../../hooks/useVaultNamedLabels';
import {
  addVaultNamedLabelGroup,
  describeWorkspaceGroupError,
  plainStepUpError,
  useRemoveVaultNamedLabelGroup,
  useVaultNamedLabelGroupPreview,
  useVaultNamedLabelGroups,
  useVaultWorkspaceGroups,
  useVaultWorkspaceGroupsInvalidator,
} from '../../hooks/useVaultWorkspaceGroups';
import {
  ACCESS_ACTIONS,
  isPrivilegedTier,
  namedLabelsPath,
  personLabel,
  TIER_NAMES,
  tierReach,
  workspaceGroupDetailPath,
  workspaceGroupsPath,
  type WorkspaceTier,
} from './shared';

export interface LabelDetailProps {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  myTier: WorkspaceTier | null;
  myPrincipalId: string | null;
  members: readonly VaultHolderMemberDto[];
}

export function LabelDetail({ holderId, label, myTier, myPrincipalId, members }: LabelDetailProps) {
  const isManager = label.myPlace?.isManager === true;
  const isOnIt = label.myPlace !== null;
  // A reader through a group has no place of their own: leaving is leaving the group.
  const viaGroup = label.myPlace?.viaGroup === true;
  const privileged = isPrivilegedTier(myTier);
  const canRetire = isManager || myTier === 'owner';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            <Lock className="h-4 w-4" aria-hidden="true" />
            {label.name}
            {label.deprecatedAt && <Badge variant="secondary">Deprecated</Badge>}
            {label.acceptsFromNonReaders && <Badge variant="outline">Drop box</Badge>}
          </CardTitle>
          {label.description && <CardDescription>{label.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Only the people on this label can see its documents — and each of them only up to their
            workspace role. Owners and admins aren’t on a label unless they’re added.
          </p>
          {label.acceptsFromNonReaders && (
            <p>
              Members not on the label can file documents into it but can’t see them afterwards.
            </p>
          )}
          {label.deprecatedAt && (
            <p>No new documents can get this label. Documents that have it keep it.</p>
          )}
          {isOnIt &&
            (viaGroup ? (
              <p className="text-foreground">
                You read this label through a group.{' '}
                <Link to={workspaceGroupsPath(holderId)} className="text-primary hover:underline">
                  See the groups
                </Link>
              </p>
            ) : (
              <p className="text-foreground">
                {isManager ? 'You manage this label.' : 'You’re on this label.'}
              </p>
            ))}
        </CardContent>
      </Card>

      {/* An owner or admin who does not manage the label may recover it —
          on it already (a plain reader made a manager) or not (added as
          one): the server allows both (ADR-039 §Owners). */}
      {privileged && !isManager && (
        <RecoverLabelCard holderId={holderId} label={label} myTier={myTier} isOnIt={isOnIt} />
      )}

      {(isOnIt || privileged) && (
        <ReadersCard
          holderId={holderId}
          label={label}
          isManager={isManager}
          viaGroup={viaGroup}
          myPrincipalId={myPrincipalId}
          members={members}
        />
      )}

      {(isOnIt || privileged) && (
        <GroupsCard
          holderId={holderId}
          label={label}
          isManager={isManager}
          canRemove={isManager || myTier === 'owner'}
          members={members}
          myPrincipalId={myPrincipalId}
        />
      )}

      {isManager && <EditLabelCard holderId={holderId} label={label} />}

      {(isManager || (label.aiMayRead && canRetire)) && (
        <AiReadingCard holderId={holderId} label={label} isManager={isManager} />
      )}

      {(isManager || privileged) && (
        <AccessLogCard holderId={holderId} labelId={label.labelId} members={members} />
      )}

      {canRetire && <RetireLabelCard holderId={holderId} label={label} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// People on the label
// ---------------------------------------------------------------------------

function ReadersCard({
  holderId,
  label,
  isManager,
  viaGroup,
  myPrincipalId,
  members,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  isManager: boolean;
  /** The caller reads it only through a group: the server refuses their "Leave". */
  viaGroup: boolean;
  myPrincipalId: string | null;
  members: readonly VaultHolderMemberDto[];
}) {
  const readers = useVaultNamedLabelReaders(holderId, label.labelId);
  const setManager = useSetVaultNamedLabelManager(holderId);
  const remove = useRemoveVaultNamedLabelReader(holderId);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const onError = (title: string) => (err: unknown) =>
    toast({ title, description: describeNamedLabelError(err), variant: 'destructive' });

  const all = readers.data ?? [];
  const managerCount = all.filter((r) => r.isManager).length;

  /** Removing someone (or leaving) takes effect at once: ask first, and say what it costs. */
  const confirmRemoval = async (r: VaultNamedLabelReaderDto, isMe: boolean): Promise<boolean> => {
    const lastManager = r.isManager && managerCount <= 1;
    const lastReader = all.length <= 1;
    const consequence = lastReader
      ? 'Nobody will be left on it: its documents stay, but no one can open them until an owner adds themselves.'
      : lastManager
        ? 'No manager will be left: nobody can add people or approve requests until an owner adds themselves.'
        : isMe
          ? 'You’ll lose access to its documents at once. A manager can add you back.'
          : 'They’ll lose access to its documents at once. You can add them back.';
    return confirm({
      title: isMe
        ? `Leave ${label.name}?`
        : `Remove ${personLabel(members, r.principalId, myPrincipalId)}?`,
      description: consequence,
      confirmLabel: isMe ? 'Leave label' : 'Remove',
      destructive: true,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>People on this label</CardTitle>
        <CardDescription>
          Each person sees labelled documents only up to their workspace role: viewers see Standard
          documents, members also Confidential, owners and admins every tier.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {readers.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : readers.isError ? (
          <p className="text-sm text-destructive">{describeNamedLabelError(readers.error)}</p>
        ) : (readers.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody is on this label. An owner can add themselves.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {(readers.data ?? []).map((r) => {
              const isMe = r.principalId === myPrincipalId;
              return (
                <li
                  key={r.readerId}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      {personLabel(members, r.principalId, myPrincipalId)}
                      {r.isManager && <Badge variant="outline">Manager</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {TIER_NAMES[r.tier]} · sees {tierReach(r.tier).toLowerCase()}
                      {r.expiresAt && ` · until ${new Date(r.expiresAt).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {isManager && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={setManager.isPending}
                        onClick={async () => {
                          if (
                            r.isManager &&
                            !(await confirm({
                              title: `Stop ${isMe ? 'managing' : `${personLabel(members, r.principalId, myPrincipalId)} managing`} ${label.name}?`,
                              description: isMe
                                ? 'You’ll stay on the label but can no longer add people or approve requests.'
                                : 'They’ll stay on the label but can no longer add people or approve requests.',
                              confirmLabel: 'Stop managing',
                            }))
                          ) {
                            return;
                          }
                          setManager.mutate(
                            {
                              labelId: label.labelId,
                              principalId: r.principalId,
                              isManager: !r.isManager,
                            },
                            { onError: onError('Could not change who manages this label') },
                          );
                        }}
                      >
                        {r.isManager ? 'Stop managing' : 'Make manager'}
                      </Button>
                    )}
                    {(isManager || (isMe && !viaGroup)) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={async () => {
                          if (!(await confirmRemoval(r, isMe))) return;
                          remove.mutate(
                            { labelId: label.labelId, principalId: r.principalId },
                            {
                              onSuccess: () => {
                                toast({
                                  title: isMe ? 'You left the label' : 'Removed from the label',
                                });
                                if (isMe) navigate(namedLabelsPath(holderId));
                              },
                              onError: onError(isMe ? 'Could not leave' : 'Could not remove'),
                            },
                          );
                        }}
                      >
                        {isMe ? 'Leave' : 'Remove'}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {isManager && !label.deprecatedAt && (
          <AddReaderForm
            holderId={holderId}
            label={label}
            members={members}
            onList={(readers.data ?? []).map((r) => r.principalId)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AddReaderForm({
  holderId,
  label,
  members,
  onList,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  members: readonly VaultHolderMemberDto[];
  onList: string[];
}) {
  const invalidate = useVaultNamedLabelsInvalidator(holderId);
  const [principalId, setPrincipalId] = useState('');
  const [asManager, setAsManager] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const candidates = members.filter(
    (m) => m.status === 'active' && !onList.includes(m.principalId),
  );
  const chosen = members.find((m) => m.principalId === principalId);

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      <Label htmlFor="named-label-add-reader">Add someone</Label>
      <div className="flex flex-wrap items-center gap-3">
        <select
          id="named-label-add-reader"
          value={principalId}
          onChange={(e) => setPrincipalId(e.target.value)}
          className="min-w-[14rem] rounded border border-border bg-card px-2 py-1.5 text-sm"
        >
          <option value="">Choose a person…</option>
          {candidates.map((m) => (
            <option key={m.principalId} value={m.principalId}>
              {personLabel(members, m.principalId)} ({TIER_NAMES[m.role]})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={asManager} onCheckedChange={setAsManager} />
          Can manage the label
        </label>
        <Button size="sm" disabled={!principalId} onClick={() => setConfirming(true)}>
          Add
        </Button>
      </div>
      {chosen && (
        <p className="text-xs text-muted-foreground">
          As {TIER_NAMES[chosen.role].toLowerCase()}, they’ll see{' '}
          {tierReach(chosen.role).toLowerCase()} with this label.
        </p>
      )}
      <StepUpDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Add ${chosen ? personLabel(members, chosen.principalId) : 'this person'} to ${label.name}?`}
        description="They’ll be able to see this label’s documents up to their workspace role. The people on the label are told. Confirm it’s you to continue."
        confirmLabel="Add to label"
        onConfirm={async (creds) => {
          await addVaultNamedLabelReader(
            holderId,
            label.labelId,
            { principalId, ...(asManager ? { isManager: true } : {}) },
            creds,
          );
        }}
        onSuccess={() => {
          toast({ title: 'Added to the label' });
          setPrincipalId('');
          setAsManager(false);
          void invalidate();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups that read the label (ADR-039 amendment 1)
// ---------------------------------------------------------------------------

function GroupsCard({
  holderId,
  label,
  isManager,
  canRemove,
  members,
  myPrincipalId,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  isManager: boolean;
  canRemove: boolean;
  members: readonly VaultHolderMemberDto[];
  myPrincipalId: string | null;
}) {
  const groups = useVaultNamedLabelGroups(holderId, label.labelId);
  const remove = useRemoveVaultNamedLabelGroup(holderId);
  const confirm = useConfirm();

  /** Taking a group off ends every member's reading at once: ask first, and say so. */
  const confirmRemoval = (g: VaultNamedLabelGroupDto): Promise<boolean> => {
    const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;
    return confirm({
      title: `Take ${g.name} off ${label.name}?`,
      description:
        `${people(g.readerCount)} reading through it will lose access to its documents at once.` +
        (g.pendingCount > 0
          ? ` ${people(g.pendingCount)} waiting for approval will be withdrawn.`
          : '') +
        ' Putting the group back means reviewing its people again.',
      confirmLabel: 'Take off',
      destructive: true,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          Groups
        </CardTitle>
        <CardDescription>
          Everyone in the group can read this label’s documents, each up to their workspace role.
          People added to the group later need a manager of this label to approve them, unless a
          manager of this label adds them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {groups.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.isError ? (
          <p className="text-sm text-destructive">{describeWorkspaceGroupError(groups.error)}</p>
        ) : (groups.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No groups read this label.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {(groups.data ?? []).map((g) => (
              <li
                key={g.labelGroupId}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <Link
                    to={workspaceGroupDetailPath(holderId, g.groupId)}
                    className="font-medium text-foreground hover:underline"
                  >
                    {g.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {g.readerCount} reading
                    {g.pendingCount > 0 && ` · ${g.pendingCount} waiting for approval`}
                  </p>
                </div>
                {canRemove && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={async () => {
                      if (!(await confirmRemoval(g))) return;
                      remove.mutate(
                        { labelId: label.labelId, groupId: g.groupId },
                        {
                          onSuccess: () => toast({ title: 'Group taken off the label' }),
                          onError: (err) =>
                            toast({
                              title: 'Could not take the group off',
                              description: describeWorkspaceGroupError(err),
                              variant: 'destructive',
                            }),
                        },
                      );
                    }}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {isManager && !label.deprecatedAt && (
          <AddGroupForm
            holderId={holderId}
            label={label}
            onLabel={(groups.data ?? []).map((g) => g.groupId)}
            members={members}
            myPrincipalId={myPrincipalId}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Put a group on the label. Choosing a group first shows who it would let in
 * (the preview: each person with their workspace role, owners and admins
 * called out), then asks for step-up. The add is pinned to the people shown
 * (`expectedMemberIds`): if the group changed meanwhile, the server refuses
 * `NAMED_LABEL_GROUP_CHANGED` and the list is loaded again to review.
 */
function AddGroupForm({
  holderId,
  label,
  onLabel,
  members,
  myPrincipalId,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  onLabel: string[];
  members: readonly VaultHolderMemberDto[];
  myPrincipalId: string | null;
}) {
  const workspaceGroups = useVaultWorkspaceGroups(holderId);
  const invalidate = useVaultWorkspaceGroupsInvalidator(holderId);
  const [groupId, setGroupId] = useState('');
  /** The group whose people are being reviewed (null: not reviewing). */
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  /** Exactly what the manager reviewed when they pressed Continue — what is submitted. */
  const [reviewed, setReviewed] = useState<{ groupId: string; memberIds: string[] } | null>(null);
  const [changed, setChanged] = useState(false);
  const changedRef = useRef(false);
  const preview = useVaultNamedLabelGroupPreview(holderId, label.labelId, reviewing);
  const candidates = (workspaceGroups.data ?? []).filter((g) => !onLabel.includes(g.groupId));
  const chosen = candidates.find((g) => g.groupId === reviewing);
  const shown = preview.data?.members ?? [];
  const groupName = preview.data?.name ?? chosen?.name ?? 'this group';
  const newcomers = shown.filter((m) => !m.alreadyReads).length;
  // A group too big for the label's room is said before step-up, not refused after it.
  const overLimit = preview.data ? preview.data.readersAfter > preview.data.readerLimit : false;

  const stopReviewing = () => {
    setReviewing(null);
    setReviewed(null);
    setConfirming(false);
    setChanged(false);
  };

  // If the list on screen moves while step-up is open, what they confirm would
  // no longer be what they reviewed: close it and ask for a fresh look.
  const liveIds = shown
    .map((m) => m.principalId)
    .sort()
    .join(',');
  useEffect(() => {
    if (!confirming || !reviewed || !preview.data) return;
    if (liveIds !== [...reviewed.memberIds].sort().join(',')) {
      setConfirming(false);
      setReviewed(null);
      setChanged(true);
    }
  }, [confirming, reviewed, liveIds, preview.data]);

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      <Label htmlFor="named-label-add-group">Add a group</Label>
      {workspaceGroups.isSuccess && candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Every group in this workspace already reads this label, or there are no groups yet.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            id="named-label-add-group"
            value={groupId}
            onChange={(e) => {
              setGroupId(e.target.value);
              stopReviewing();
            }}
            className="min-w-[14rem] rounded border border-border bg-card px-2 py-1.5 text-sm"
          >
            <option value="">Choose a group…</option>
            {candidates.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={!groupId || reviewing !== null}
            onClick={() => {
              setChanged(false);
              setReviewing(groupId);
            }}
          >
            Add group
          </Button>
        </div>
      )}

      {reviewing && (
        <section
          aria-label={`Who ${groupName} lets read ${label.name}`}
          className="space-y-3 rounded-md border border-border bg-card p-3 text-sm"
        >
          {changed && (
            <p className="text-amber-700 dark:text-amber-400">
              The group’s members changed while you were looking. Review them again.
            </p>
          )}
          {preview.isLoading || preview.isFetching ? (
            <p className="text-muted-foreground">Loading who’s in the group…</p>
          ) : preview.isError ? (
            <p className="text-destructive">{describeWorkspaceGroupError(preview.error)}</p>
          ) : (
            <>
              <p className="text-foreground">
                Everyone below will be able to read this label’s documents, each up to their
                workspace role. People added to {groupName} later need a manager of this label to
                approve them, unless a manager of this label adds them.
              </p>
              {shown.length === 0 ? (
                <p className="text-muted-foreground">Nobody is in {groupName} yet.</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {shown.map((m) => (
                    <li key={m.principalId} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                        {personLabel(members, m.principalId, myPrincipalId)}
                        {isPrivilegedTier(m.tier) && (
                          <Badge variant="destructive">{TIER_NAMES[m.tier]}</Badge>
                        )}
                        {m.alreadyReads && <Badge variant="secondary">Already on this label</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {isPrivilegedTier(m.tier)
                          ? `${TIER_NAMES[m.tier]} — will be able to read this label, at every tier.`
                          : `${TIER_NAMES[m.tier]} — ${tierReach(m.tier).toLowerCase()}.`}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {overLimit && preview.data && (
                <p className="text-destructive">
                  With {groupName} on it, {preview.data.readersAfter} people would read this label.
                  A label holds at most {preview.data.readerLimit}. Take people off the label or out
                  of the group first.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={overLimit}
                  onClick={() => {
                    if (!preview.data || !reviewing) return;
                    setReviewed({
                      groupId: reviewing,
                      memberIds: preview.data.members.map((m) => m.principalId),
                    });
                    setChanged(false);
                    setConfirming(true);
                  }}
                >
                  {newcomers > 0
                    ? `Continue — let ${newcomers} ${newcomers === 1 ? 'person' : 'people'} in`
                    : 'Continue'}
                </Button>
                <Button size="sm" variant="ghost" onClick={stopReviewing}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      <StepUpDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Let ${groupName} read ${label.name}?`}
        description={`Everyone in the group you just reviewed (${reviewed?.memberIds.length ?? 0} ${
          reviewed?.memberIds.length === 1 ? 'person' : 'people'
        }) will be able to see this label’s documents, each up to their workspace role. The people on the label are told. Confirm it’s you to continue.`}
        confirmLabel="Add group"
        onConfirm={async (creds) => {
          if (!reviewed) return;
          changedRef.current = false;
          try {
            await addVaultNamedLabelGroup(
              holderId,
              label.labelId,
              // The frozen snapshot, never the live list: the server refuses it
              // if the group moved since the review.
              { groupId: reviewed.groupId, expectedMemberIds: reviewed.memberIds },
              creds,
            );
          } catch (err) {
            // The group moved under the manager: close, and show the new list to review.
            if (namedLabelErrorCode(err) === 'NAMED_LABEL_GROUP_CHANGED') {
              changedRef.current = true;
              return;
            }
            plainStepUpError(err);
          }
        }}
        onSuccess={() => {
          if (changedRef.current) {
            changedRef.current = false;
            setReviewed(null);
            setChanged(true);
            void preview.refetch();
            return;
          }
          toast({ title: 'Group added to the label' });
          setGroupId('');
          stopReviewing();
          void invalidate();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// An owner or admin adding themselves
// ---------------------------------------------------------------------------

/**
 * An owner or admin taking on a label they do not manage (step-up): added as
 * a manager, or — already on it — made one. An owner's is immediate; an
 * admin's is a request another owner or admin approves. Either way the
 * label's people and the other owners are told.
 */
function RecoverLabelCard({
  holderId,
  label,
  myTier,
  isOnIt,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  myTier: WorkspaceTier | null;
  isOnIt: boolean;
}) {
  const invalidate = useVaultNamedLabelsInvalidator(holderId);
  const [confirming, setConfirming] = useState(false);
  const isOwner = myTier === 'owner';
  const action = isOnIt
    ? isOwner
      ? 'Make yourself a manager'
      : 'Ask to manage it'
    : isOwner
      ? 'Add yourself'
      : 'Ask to add yourself';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          {isOnIt ? 'You don’t manage this label' : 'You’re not on this label'}
        </CardTitle>
        <CardDescription>
          {label.orphaned
            ? isOnIt
              ? 'Nobody manages this label any more. Make yourself a manager to look after it.'
              : 'Nobody manages this label any more. Add yourself to look after it.'
            : isOnIt
              ? 'As an owner or admin you can make yourself a manager.'
              : 'As an owner or admin you can add yourself as a manager.'}{' '}
          The people on the label and the other owners will be told.{' '}
          {isOwner
            ? 'It happens straight away.'
            : 'An admin’s request needs another owner or admin to approve it.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button size="sm" onClick={() => setConfirming(true)}>
          {action}
        </Button>
        <StepUpDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={isOnIt ? `Manage ${label.name}?` : `Add yourself to ${label.name}?`}
          description="The people on the label and the other owners will be told. Confirm it’s you to continue."
          confirmLabel={isOwner ? (isOnIt ? 'Make me a manager' : 'Add yourself') : 'Ask'}
          onConfirm={async (creds) => {
            const result = await recoverVaultNamedLabel(holderId, label.labelId, creds);
            toast({
              title:
                'request' in result
                  ? 'Asked — another owner or admin has to approve it'
                  : 'You manage this label now',
            });
          }}
          onSuccess={() => void invalidate()}
        />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Name, description, drop box (managers)
// ---------------------------------------------------------------------------

function EditLabelCard({
  holderId,
  label,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
}) {
  const update = useUpdateVaultNamedLabel(holderId);
  const [name, setName] = useState(label.name);
  const [description, setDescription] = useState(label.description ?? '');
  const [dropBox, setDropBox] = useState(label.acceptsFromNonReaders);
  useEffect(() => {
    setName(label.name);
    setDescription(label.description ?? '');
    setDropBox(label.acceptsFromNonReaders);
  }, [label.name, label.description, label.acceptsFromNonReaders]);

  const dirty =
    name.trim() !== label.name ||
    (description.trim() || null) !== (label.description ?? null) ||
    dropBox !== label.acceptsFromNonReaders;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Label settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!dirty || !name.trim()) return;
            update.mutate(
              {
                labelId: label.labelId,
                ...(name.trim() !== label.name ||
                (description.trim() || null) !== (label.description ?? null)
                  ? { name: name.trim(), description: description.trim() || null }
                  : {}),
                // Only a change: opening widens (the label's settings), closing
                // narrows (its own call).
                ...(dropBox !== label.acceptsFromNonReaders
                  ? { dropBox: dropBox ? ('open' as const) : ('close' as const) }
                  : {}),
              },
              {
                onSuccess: () => toast({ title: 'Label saved' }),
                onError: (err) =>
                  toast({
                    title: 'Could not save the label',
                    description: describeNamedLabelError(err),
                    variant: 'destructive',
                  }),
              },
            );
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="named-label-edit-name">Name</Label>
            <Input
              id="named-label-edit-name"
              value={name}
              maxLength={VAULT_NAMED_LABEL_NAME_MAX}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="named-label-edit-description">Description</Label>
            <Textarea
              id="named-label-edit-description"
              value={description}
              maxLength={VAULT_NAMED_LABEL_DESCRIPTION_MAX}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={dropBox} onCheckedChange={setDropBox} />
            <span>
              Drop box
              <span className="block text-xs text-muted-foreground">
                Members not on the label can file documents into it but can’t see them afterwards.
              </span>
            </span>
          </label>
          <Button type="submit" size="sm" disabled={!dirty || !name.trim() || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Fias AI (ADR-039 amendment 2)
// ---------------------------------------------------------------------------

/**
 * Whether Fias AI may read the label's documents. Letting it is a widening a
 * manager confirms with step-up; stopping it is a narrowing a manager or an
 * owner can do anywhere. The people on the label are told either way.
 */
function AiReadingCard({
  holderId,
  label,
  isManager,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
  isManager: boolean;
}) {
  const invalidate = useVaultNamedLabelsInvalidator(holderId);
  const stop = useStopVaultNamedLabelAiReading(holderId);
  const [confirming, setConfirming] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          Fias AI
        </CardTitle>
        <CardDescription>
          {label.aiMayRead
            ? 'Fias AI can read this label’s documents for the people on it. What it reads is sent to an AI provider, and what it makes from them keeps this label or isn’t saved.'
            : 'Fias AI can’t read this label’s documents, even for the people on it.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {label.aiMayRead ? (
          <Button
            size="sm"
            variant="outline"
            disabled={stop.isPending}
            onClick={() =>
              stop.mutate(label.labelId, {
                onSuccess: () => toast({ title: 'Fias AI can no longer read this label' }),
                onError: (err) =>
                  toast({
                    title: 'Could not change it',
                    description: describeNamedLabelError(err),
                    variant: 'destructive',
                  }),
              })
            }
          >
            Stop Fias AI reading it
          </Button>
        ) : (
          isManager && (
            <>
              <Button size="sm" onClick={() => setConfirming(true)}>
                Let Fias AI read it
              </Button>
              <StepUpDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={`Let Fias AI read ${label.name}?`}
                description="The people on the label can then use Fias AI with its documents, which sends their content to an AI provider. Everyone on the label will be told. Confirm it’s you to continue."
                confirmLabel="Let Fias AI read it"
                onConfirm={async (creds) => {
                  await allowVaultNamedLabelAiReading(holderId, label.labelId, creds);
                  toast({ title: 'Fias AI can now read this label' });
                }}
                onSuccess={() => void invalidate()}
              />
            </>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Who was given access
// ---------------------------------------------------------------------------

function AccessLogCard({
  holderId,
  labelId,
  members,
}: {
  holderId: string;
  labelId: string;
  members: readonly VaultHolderMemberDto[];
}) {
  const log = useVaultNamedLabelAccessLog(holderId, labelId);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Who was given access</CardTitle>
        <CardDescription>
          A download link can be passed on, so this records who was given access to this label’s
          documents — not who opened a file.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {log.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : log.isError ? (
          <p className="text-sm text-destructive">{describeNamedLabelError(log.error)}</p>
        ) : (log.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {(log.data ?? []).map((e) => (
              <li key={e.accessId} className="flex flex-wrap justify-between gap-2">
                <span>
                  <span className="font-medium text-foreground">
                    {personLabel(members, e.principalId)}
                  </span>{' '}
                  {ACCESS_ACTIONS[e.action]}{' '}
                  <span className="text-muted-foreground">
                    {e.documentId ? `(document …${e.documentId.slice(-6)})` : '(a document)'}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(e.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Deprecate / delete
// ---------------------------------------------------------------------------

function RetireLabelCard({
  holderId,
  label,
}: {
  holderId: string;
  label: VaultNamedLabelSummaryDto;
}) {
  const deprecate = useDeprecateVaultNamedLabel(holderId);
  const remove = useDeleteVaultNamedLabel(holderId);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const onDeprecate = async () => {
    const ok = await confirm({
      title: `Deprecate ${label.name}?`,
      description:
        'No new documents will be able to get this label. Documents that have it keep it.',
      confirmLabel: 'Deprecate',
    });
    if (!ok) return;
    deprecate.mutate(label.labelId, {
      onSuccess: () => toast({ title: 'Label deprecated' }),
      onError: (err) =>
        toast({
          title: 'Could not deprecate the label',
          description: describeNamedLabelError(err),
          variant: 'destructive',
        }),
    });
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete ${label.name}?`,
      description:
        'The label is removed and everyone on it is taken off. Its history — who was on it and who ' +
        'was given access — is kept. This can’t be undone.',
      confirmLabel: 'Delete label',
      destructive: true,
    });
    if (!ok) return;
    remove.mutate(label.labelId, {
      onSuccess: () => {
        toast({ title: 'Label deleted' });
        navigate(namedLabelsPath(holderId));
      },
      onError: (err) =>
        toast({
          title: 'Could not delete the label',
          description: describeNamedLabelError(err),
          variant: 'destructive',
        }),
    });
  };

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-base">Retire this label</CardTitle>
        <CardDescription>
          Deprecating stops new documents getting the label; documents that have it keep it. A label
          can only be deleted once no document has it.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {!label.deprecatedAt && (
          <Button size="sm" variant="outline" disabled={deprecate.isPending} onClick={onDeprecate}>
            Deprecate
          </Button>
        )}
        <Button size="sm" variant="destructive" disabled={remove.isPending} onClick={onDelete}>
          Delete
        </Button>
      </CardContent>
    </Card>
  );
}
